<script setup lang="ts">
type DataRequestData = App.DataRequests.Data.DataRequestData;
type EnumData = App.Base.Data.EnumData;
type ApplicationData = App.Students.Data.ApplicationData;

import type { HybridlyFormData } from '@/@types/global';

import { useDataRequestIndicators } from '@/hooks/useDataRequestIndicators';

import Alert from '@/components/alert/Alert.vue';
import FormInstituteInput from '@/components/formComboboxInput/FormInstituteInput.vue';
import FormListboxInput from '@/components/formListboxInput/FormListboxInput.vue';
import FormRadioInput from '@/components/formRadioInput/FormRadioInput.vue';
import FormRows from '@/components/formRows/FormRows.vue';
import FormTextareaInput from '@/components/formTextareaInput/FormTextareaInput.vue';

import TimesCircle from '@/icons/line/times-circle.svg';
import Fieldset from '@/components/fieldset/Fieldset.vue';
import FormCheckboxInput from '@/components/formCheckboxInput/FormCheckboxInput.vue';

const props = defineProps<{
  dataRequest?: DataRequestData;
  form: HybridlyFormData<{
    application: {
      student_id: string;
      institute_id: number;
      date_submitted: string;
      status: EnumData;
      intend_to_enroll: EnumData;
      has_waiver: EnumData;
      first_choice: boolean;
      early_action: boolean;
      early_decision: boolean;
      sent_hs_transcript: boolean;
      applied_to_honors_program: boolean;
      financial_aid_submitted_to_school: boolean;
    };
    user_comment: string;
  }>;
  application: ApplicationData;
  statuses: EnumData[];
}>();

const { computedStatusBadge, hasAttributeChange } = useDataRequestIndicators({
  dataRequest: props.dataRequest,
  base: props.application
});

const showIntendToEnroll = computed(
  () =>
    props.form.fields.application.status?.value === 'accepted' ||
    props.form.fields.application.intend_to_enroll === true
);

function handleStatusChange(status) {
  props.form.fields.application.status = status;
  props.form.fields.application.intend_to_enroll = false;
}

function handleInstituteChange(institute) {
  props.form.fields.application.institute_id = institute;
}
</script>

<template>
  <div class="-mx-5 -mb-8 -mt-6 flex flex-col divide-y divide-slate-200">
    <div class="px-5 pb-5 pt-6" v-if="dataRequest?.status.value === 'rejected'">
      <Alert
        v-if="dataRequest?.status.value === 'rejected'"
        :description="`Reason: ${dataRequest.rejected_reason ?? 'No reason provided'}`"
        :icon="TimesCircle"
        title="Submission was not approved"
        color="danger"
        variant="soft"
      />
    </div>
    <FormRows class="px-5 pb-8 pt-6">
      <FormInstituteInput
        :error="form.errors.application?.institute_id"
        :modelValue="form.fields.application.institute_id"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="
          hasAttributeChange('institute.id', 'institute_id') ? computedStatusBadge.label : undefined
        "
        label="College or program name"
        @update:modelValue="handleInstituteChange"
      />
      <FormListboxInput
        :error="form.errors.application?.status"
        :modelValue="form.fields.application.status"
        :options="statuses"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="hasAttributeChange('status') ? computedStatusBadge.label : undefined"
        label="Status"
        @update:modelValue="handleStatusChange"
      />
      <FormRadioInput
        v-if="showIntendToEnroll"
        label="Do you intend to enroll here?"
        :error="form.errors.application?.intend_to_enroll"
        :isFieldset="false"
        :radioInputs="[
          {
            label: 'Yes, I intend to enroll',
            value: true
          },
          {
            label: 'No',
            value: false
          }
        ]"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="
          hasAttributeChange('intend_to_enroll') ? computedStatusBadge.label : undefined
        "
        :value="form.fields.application.intend_to_enroll"
        ariaLabel="Intend to enroll"
        name="intend_to_enroll"
        variant="outlined"
        @update:modelValue="(value) => (form.fields.application.intend_to_enroll = value)"
      />
      <Fieldset title="Select all that apply:">
        <FormCheckboxInput
          :checked="form.fields.application.has_waiver"
          :error="form.errors.application?.has_waiver"
          :statusBadgeColor="computedStatusBadge.color"
          :statusBadgeIcon="computedStatusBadge.icon"
          :statusBadgeLabel="
            hasAttributeChange('has_waiver') ? computedStatusBadge.label : undefined
          "
          label="I used a college application waiver"
          name="has_waiver"
          @update:checked="(value) => (form.fields.application.has_waiver = value)"
        />
        <FormCheckboxInput
          :checked="form.fields.application.first_choice"
          :error="form.errors.application?.first_choice"
          :statusBadgeColor="computedStatusBadge.color"
          :statusBadgeIcon="computedStatusBadge.icon"
          :statusBadgeLabel="
            hasAttributeChange('first_choice') ? computedStatusBadge.label : undefined
          "
          label="This college/program is my top choice"
          name="first_choice"
          @update:checked="(value) => (form.fields.application.first_choice = value)"
        />
        <FormCheckboxInput
          :checked="form.fields.application.sent_hs_transcript"
          :error="form.errors.application?.sent_hs_transcript"
          :statusBadgeColor="computedStatusBadge.color"
          :statusBadgeIcon="computedStatusBadge.icon"
          :statusBadgeLabel="
            hasAttributeChange('sent_hs_transcript') ? computedStatusBadge.label : undefined
          "
          label="I sent the college my final high school transcript"
          name="sent_hs_transcript"
          @update:checked="(value) => (form.fields.application.sent_hs_transcript = value)"
        />
        <FormCheckboxInput
          :checked="form.fields.application.financial_aid_submitted_to_school"
          :error="form.errors.application?.financial_aid_submitted_to_school"
          :statusBadgeColor="computedStatusBadge.color"
          :statusBadgeIcon="computedStatusBadge.icon"
          :statusBadgeLabel="
            hasAttributeChange('financial_aid_submitted_to_school')
              ? computedStatusBadge.label
              : undefined
          "
          label="I submitted my FASFA to the college"
          name="financial_aid_submitted_to_school"
          @update:checked="
            (value) => (form.fields.application.financial_aid_submitted_to_school = value)
          "
        />
      </Fieldset>
      <Fieldset title="Select all that apply:">
        <FormCheckboxInput
          :checked="form.fields.application.early_action"
          :error="form.errors.application?.early_action"
          :statusBadgeColor="computedStatusBadge.color"
          :statusBadgeIcon="computedStatusBadge.icon"
          :statusBadgeLabel="
            hasAttributeChange('early_action') ? computedStatusBadge.label : undefined
          "
          label="I applied using the Early Action option"
          name="early_action"
          @update:checked="(value) => (form.fields.application.early_action = value)"
        />
        <FormCheckboxInput
          :checked="form.fields.application.early_decision"
          :error="form.errors.application?.early_decision"
          :statusBadgeColor="computedStatusBadge.color"
          :statusBadgeIcon="computedStatusBadge.icon"
          :statusBadgeLabel="
            hasAttributeChange('early_decision') ? computedStatusBadge.label : undefined
          "
          label="I applied using the Early Decision option"
          name="early_decision"
          @update:checked="(value) => (form.fields.application.early_decision = value)"
        />
        <FormCheckboxInput
          :checked="form.fields.application.applied_to_honors_program"
          :error="form.errors.application?.applied_to_honors_program"
          :statusBadgeColor="computedStatusBadge.color"
          :statusBadgeIcon="computedStatusBadge.icon"
          :statusBadgeLabel="
            hasAttributeChange('applied_to_honors_program') ? computedStatusBadge.label : undefined
          "
          label="I applied to an Honors program with this application"
          name="sent_hs_transcript"
          @update:checked="(value) => (form.fields.application.applied_to_honors_program = value)"
        />
      </Fieldset>
    </FormRows>
    <FormRows class="px-5 pb-8 pt-6">
      <FormTextareaInput
        label="Note"
        helpText="Note to adviser about this submission"
        :error="form.errors.user_comment"
        :modelValue="form.fields.user_comment"
        name="user_comment"
        isOptional
        @update:modelValue="(value) => (form.fields.user_comment = value)"
      />
    </FormRows>
  </div>
</template>
